import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  FileText, FolderOpen, Users, Mail, Plus, MessageSquare,
  TrendingUp, Eye, Clock, CheckCircle, AlertCircle, ArrowRight,
  BarChart3, Calendar, Send
} from "lucide-react";
import api from "@/lib/api";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewsPeriod, setViewsPeriod] = useState("all");
  const [viewsData, setViewsData] = useState(null);

  useEffect(() => {
    api.get("/stats/dashboard")
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get(`/stats/views?period=${viewsPeriod}`)
      .then((res) => setViewsData(res.data))
      .catch(console.error);
  }, [viewsPeriod]);

  if (loading) {
    return (
      <div data-testid="admin-dashboard" className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h1 className="font-heading text-3xl">Tableau de bord</h1></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border rounded-none animate-pulse">
              <CardContent className="p-4"><div className="h-14 bg-muted rounded" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const s = data?.stats || {};
  const kpis = [
    { label: "Articles publiés", value: s.published || 0, icon: FileText, color: "text-sky-500", bg: "bg-sky-500/10" },
    { label: "Vues totales", value: s.total_views || 0, icon: TrendingUp, color: "text-violet-500", bg: "bg-violet-500/10" },
    { label: "Brouillons", value: s.drafts || 0, icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
    { label: "En révision", value: s.in_review || 0, icon: Eye, color: "text-indigo-500", bg: "bg-indigo-500/10" },
    { label: "Abonnés", value: s.subscribers_total || 0, icon: Mail, color: "text-emerald-500", bg: "bg-emerald-500/10", sub: s.subscribers_recent ? `+${s.subscribers_recent} cette semaine` : null },
    { label: "Messages", value: s.messages_unread || 0, icon: MessageSquare, color: "text-rose-500", bg: "bg-rose-500/10", sub: s.messages_unread > 0 ? "non lus" : "tout lu" },
  ];

  const statusMap = {
    published: { label: "Publié", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    draft: { label: "Brouillon", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    scheduled: { label: "Programmé", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  };

  const reviewMap = {
    in_review: { label: "En révision", cls: "bg-blue-100 text-blue-800" },
    approved: { label: "Approuvé", cls: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Rejeté", cls: "bg-rose-100 text-rose-800" },
    changes_requested: { label: "Modifications", cls: "bg-amber-100 text-amber-800" },
  };

  const monthlyMax = Math.max(...(data?.monthly_activity || []).map(m => m.count), 1);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[#0f2b4c]">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">Vue d'ensemble de votre plateforme éditoriale</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/calendrier">
            <Button variant="outline" className="rounded-none border-[#0f2b4c]/20 text-[#0f2b4c] hover:bg-[#0f2b4c]/5" data-testid="calendar-btn">
              <Calendar className="h-4 w-4 mr-2" /> Calendrier
            </Button>
          </Link>
          <Link to="/admin/articles/new">
            <Button className="rounded-none bg-[#0f2b4c] hover:bg-[#0f2b4c]/90 text-white" data-testid="new-article-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouvel article
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border rounded-none bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className={`inline-flex items-center justify-center h-9 w-9 rounded-sm ${kpi.bg} mb-3`}>
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
              <p className="text-2xl font-semibold tracking-tight text-[#0f2b4c]">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi.label}</p>
              {kpi.sub && <p className="text-[10px] text-muted-foreground mt-1 font-medium">{kpi.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Grid: Activity Chart + Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <Card className="border rounded-none lg:col-span-2 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-[#0f2b4c]">
                <BarChart3 className="h-4 w-4 text-sky-500" /> Activité éditoriale
              </CardTitle>
              <span className="text-xs text-muted-foreground">6 derniers mois</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3 h-32 mt-2" data-testid="activity-chart">
              {(data?.monthly_activity || []).map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-foreground">{m.count}</span>
                  <div
                    className="w-full bg-[#0f2b4c]/80 rounded-sm transition-all duration-500 min-h-[4px]"
                    style={{ height: `${Math.max((m.count / monthlyMax) * 100, 4)}%` }}
                  />
                  <span className="text-[10px] text-muted-foreground">{m.month}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border rounded-none bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-[#0f2b4c]">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/admin/articles/new" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-new-article">
                <Plus className="h-4 w-4 mr-2 text-sky-500" /> Nouvel article
              </Button>
            </Link>
            <Link to="/admin/articles" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-articles-list">
                <FileText className="h-4 w-4 mr-2 text-sky-500" /> Gérer les articles
              </Button>
            </Link>
            <Link to="/admin/categories" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-categories">
                <FolderOpen className="h-4 w-4 mr-2 text-sky-500" /> Catégories
              </Button>
            </Link>
            <Link to="/admin/newsletter" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-newsletter">
                <Mail className="h-4 w-4 mr-2 text-sky-500" /> Newsletter
              </Button>
            </Link>
            <Link to="/admin/messages" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-messages">
                <MessageSquare className="h-4 w-4 mr-2 text-sky-500" /> Messages
                {s.messages_unread > 0 && (
                  <span className="ml-auto bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{s.messages_unread}</span>
                )}
              </Button>
            </Link>
            <Link to="/admin/calendrier" className="block">
              <Button variant="outline" className="w-full justify-start rounded-none text-sm h-10 border-[#0f2b4c]/10 hover:bg-[#0f2b4c]/5" data-testid="quick-calendar">
                <Calendar className="h-4 w-4 mr-2 text-sky-500" /> Calendrier éditorial
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Lower Grid: Top Viewed + Articles + Review + Subscribers + Messages */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Viewed Articles — with period filter */}
        <Card className="border rounded-none bg-white shadow-sm" data-testid="top-viewed-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-[#0f2b4c]">
                <TrendingUp className="h-4 w-4 text-violet-500" /> Articles les plus vus
              </CardTitle>
              <div className="flex gap-1" data-testid="views-period-filter">
                {[
                  { value: "7d", label: "7j" },
                  { value: "30d", label: "30j" },
                  { value: "all", label: "Tout" },
                ].map((p) => (
                  <Button
                    key={p.value}
                    variant={viewsPeriod === p.value ? "default" : "ghost"}
                    size="sm"
                    className={`rounded-none text-xs h-7 px-2.5 ${viewsPeriod === p.value ? "bg-[#0f2b4c] text-white" : ""}`}
                    onClick={() => setViewsPeriod(p.value)}
                    data-testid={`views-period-${p.value}`}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
            {viewsData && (
              <p className="text-xs text-muted-foreground mt-1">
                {viewsData.total_views} vue{viewsData.total_views !== 1 ? "s" : ""} {viewsPeriod === "7d" ? "ces 7 derniers jours" : viewsPeriod === "30d" ? "ces 30 derniers jours" : "au total"}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {/* Mini daily chart */}
            {viewsData?.daily && viewsData.daily.length > 0 && (
              <div className="mb-4">
                <div className="flex items-end gap-px h-16" data-testid="views-daily-chart">
                  {viewsData.daily.map((d, i) => {
                    const max = Math.max(...viewsData.daily.map(x => x.count), 1);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count} vue${d.count !== 1 ? "s" : ""}`}>
                        <div
                          className="w-full bg-violet-400/80 rounded-t-sm min-h-[2px] transition-all"
                          style={{ height: `${Math.max((d.count / max) * 100, 3)}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-muted-foreground">{viewsData.daily[0]?.date}</span>
                  <span className="text-[9px] text-muted-foreground">{viewsData.daily[viewsData.daily.length - 1]?.date}</span>
                </div>
              </div>
            )}
            <div className="space-y-1" data-testid="top-viewed-list">
              {(viewsData?.top_viewed || data?.top_viewed || []).map((article, i) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  target="_blank"
                  className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-sm hover:bg-accent transition-colors group"
                  data-testid={`top-viewed-${i}`}
                >
                  <span className="text-lg font-heading text-muted-foreground/40 w-6 text-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{article.title}</p>
                  </div>
                  <div className="flex items-center gap-1 text-violet-600 bg-violet-50 px-2 py-0.5 rounded-sm shrink-0">
                    <Eye className="h-3 w-3" />
                    <span className="text-xs font-medium">{article.views}</span>
                  </div>
                </Link>
              ))}
              {(viewsData?.top_viewed || data?.top_viewed || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucune vue enregistrée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Articles */}
        <Card className="border rounded-none bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg text-[#0f2b4c]">Articles récents</CardTitle>
              <Link to="/admin/articles" className="text-xs text-sky-500 hover:underline flex items-center gap-1">Voir tout <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="recent-articles-list">
              {(data?.recent_articles || []).map((article) => (
                <Link
                  key={article.id}
                  to={`/admin/articles/${article.id}/edit`}
                  className="flex items-center justify-between py-2.5 px-2 -mx-2 rounded-sm hover:bg-accent transition-colors group"
                  data-testid={`recent-article-${article.id}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{article.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${statusMap[article.status]?.cls || "bg-muted text-muted-foreground"}`}>
                        {statusMap[article.status]?.label || article.status}
                      </span>
                      {article.author?.name && <span className="text-[10px] text-muted-foreground">{article.author.name}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {article.publication_date ? new Date(article.publication_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}
                  </span>
                </Link>
              ))}
              {(data?.recent_articles || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucun article</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Articles in Review */}
        <Card className="border rounded-none bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-[#0f2b4c]">
                <Eye className="h-4 w-4 text-indigo-500" /> En attente de validation
              </CardTitle>
              {(data?.review_articles || []).length > 0 && (
                <Badge variant="outline" className="rounded-none text-blue-600 border-blue-200 bg-blue-50 text-xs">{data.review_articles.length}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="review-articles-list">
              {(data?.review_articles || []).map((article) => (
                <Link
                  key={article.id}
                  to={`/admin/articles/${article.id}/edit`}
                  className="flex items-center justify-between py-2.5 px-2 -mx-2 rounded-sm hover:bg-accent transition-colors group"
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{article.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {article.review_status && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${reviewMap[article.review_status]?.cls || "bg-muted"}`}>
                          {reviewMap[article.review_status]?.label || article.review_status}
                        </span>
                      )}
                      {article.author?.name && <span className="text-[10px] text-muted-foreground">{article.author.name}</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`review-article-${article.id}`}>
                    Examiner
                  </Button>
                </Link>
              ))}
              {(data?.review_articles || []).length === 0 && (
                <div className="flex flex-col items-center py-6 text-center">
                  <CheckCircle className="h-8 w-8 text-emerald-400 mb-2" />
                  <p className="text-sm text-muted-foreground">Aucun article en attente</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Subscribers */}
        <Card className="border rounded-none bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-[#0f2b4c]">
                <Send className="h-4 w-4 text-emerald-500" /> Derniers abonnés
              </CardTitle>
              <Link to="/admin/newsletter" className="text-xs text-sky-500 hover:underline flex items-center gap-1">Voir tout <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="recent-subscribers-list">
              {(data?.recent_subscribers || []).map((sub) => (
                <div key={sub.id} className="flex items-center justify-between py-2.5 px-2 -mx-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-medium shrink-0">
                      {(sub.name || sub.email)?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{sub.name || "—"}</p>
                      <p className="text-xs text-muted-foreground truncate">{sub.email}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                    {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}
                  </span>
                </div>
              ))}
              {(data?.recent_subscribers || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucun abonné</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card className="border rounded-none bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-[#0f2b4c]">
                <MessageSquare className="h-4 w-4 text-rose-500" /> Messages récents
              </CardTitle>
              <Link to="/admin/messages" className="text-xs text-sky-500 hover:underline flex items-center gap-1">Voir tout <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="recent-messages-list">
              {(data?.recent_messages || []).map((msg) => (
                <div key={msg.id} className="flex items-start gap-3 py-2.5 px-2 -mx-2">
                  <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${msg.read ? "bg-muted-foreground/30" : "bg-rose-500"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{msg.name}</p>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {msg.created_at ? new Date(msg.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}
                      </span>
                    </div>
                    {msg.subject && <p className="text-xs text-foreground/80 truncate">{msg.subject}</p>}
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{msg.message}</p>
                  </div>
                </div>
              ))}
              {(data?.recent_messages || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucun message</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
